<p-dialog
  [visible]="visible()"
  (onHide)="cancel()"
  [modal]="true"
  [closable]="false"
  [style]="{'width': '560px', 'min-height': '420px'}"
  aria-label="blackout-period-dialog"
  data-testid="blackout-period-dialog">

  <!-- Custom Header -->
  <ng-template pTemplate="header">
    <div class="flex justify-content-between align-items-center w-full px-4 py-3 border-bottom-1 surface-border">
      <div class="flex align-items-center gap-2">
        <i class="pi pi-calendar-times text-lg text-primary"></i>
        <div>
          <h3 class="m-0 text-sm font-semibold text-900" data-testid="blackout-period-create-title">
            {{ (activeConfig() ? 'CAMPAIGN.BLACKOUT.EDIT' : 'CAMPAIGN.BLACKOUT.ADD') | translate }}
          </h3>
          <p class="text-500 text-xs mt-0 mb-0">
            {{ (activeConfig() ? 'CAMPAIGN.BLACKOUT.EDIT_SUBTITLE' : 'CAMPAIGN.BLACKOUT.ADD_SUBTITLE') | translate }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="p-ripple p-button p-component p-button-icon-only p-button-secondary p-button-rounded p-button-text"
        (click)="cancel()"
        aria-label="close-blackout-period-create-button"
        data-testid="close-blackout-period-create-button">
        <span class="p-button-icon pi pi-times"></span>
      </button>
    </div>
  </ng-template>

  <!-- Body -->
  @if (form) {
    <form [formGroup]="form" (ngSubmit)="submit()" data-testid="blackout-period-form">
      <div class="flex flex-column gap-3 px-4 py-3" data-testid="blackout-period-create-content">

        <!-- Type -->
        <p-select
          inputId="blackout_type"
          [options]="typeOptions"
          optionLabel="label"
          formControlName="type"
          styleClass="w-full"
          [placeholder]="'CAMPAIGN.BLACKOUT.SELECT_TYPE' | translate"
          (onChange)="onTypeChange()"
          aria-label="blackout-type-select"
          data-testid="blackout-type-select">
        </p-select>
        @if (TypeControl?.touched && TypeControl?.errors?.['required']) {
          <small class="p-error" data-testid="blackout-type-error">
            {{ 'CAMPAIGN.BLACKOUT.TYPE_REQUIRED' | translate }}
          </small>
        }

        <!-- Names row -->
        <div class="grid m-0 gap-3">
          <div [class]="isSecondaryLanguage() ? 'col p-0' : 'col-12 p-0'">
            <input
              id="name_pri_lang"
              pInputText
              type="text"
              formControlName="name_pri_lang"
              class="w-full text-sm"
              [placeholder]="(isSecondaryLanguage()
                ? ('CAMPAIGN.BLACKOUT.NAME_PRIMARY' | translate) + ' (' + primaryLanguage() + ') *'
                : ('CAMPAIGN.BLACKOUT.NAME_PRIMARY' | translate) + ' *')"
              aria-label="blackout-name-primary-input"
              data-testid="blackout-name-primary-input" />
            @if (NamePrimary?.touched && NamePrimary?.errors?.['required']) {
              <small class="p-error block mt-1" data-testid="blackout-name-primary-error">
                {{ 'CAMPAIGN.BLACKOUT.NAME_REQUIRED' | translate }}
              </small>
            }
          </div>
          @if (isSecondaryLanguage()) {
            <div class="col p-0">
              <input
                id="name_sec_lang"
                pInputText
                type="text"
                formControlName="name_sec_lang"
                class="w-full text-sm"
                [placeholder]="('CAMPAIGN.BLACKOUT.NAME_SECONDARY' | translate) + ' (' + secondaryLanguage() + ')'"
                aria-label="blackout-name-secondary-input"
                data-testid="blackout-name-secondary-input" />
            </div>
          }
        </div>

        <!-- Date Range (type = Date) -->
        @if (SelectedType === 'Date') {
          <div class="grid m-0 gap-3">
            <div class="col p-0">
              <p-datepicker
                inputId="start_date"
                formControlName="start_date"
                inputStyleClass="w-full text-sm"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                appendTo="body"
                [placeholder]="'CAMPAIGN.BLACKOUT.START_DATE' | translate"
                aria-label="blackout-start-date-input"
                data-testid="blackout-start-date-input">
              </p-datepicker>
            </div>
            <div class="col p-0">
              <p-datepicker
                inputId="end_date"
                formControlName="end_date"
                inputStyleClass="w-full text-sm"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                appendTo="body"
                [placeholder]="'CAMPAIGN.BLACKOUT.END_DATE' | translate"
                aria-label="blackout-end-date-input"
                data-testid="blackout-end-date-input">
              </p-datepicker>
            </div>
          </div>
        }

        <!-- Time Range (type = Time) -->
        @if (SelectedType === 'Time') {
          <div class="grid m-0 gap-3">
            <div class="col p-0">
              <p-datepicker
                inputId="start_time"
                formControlName="start_time"
                inputStyleClass="w-full text-sm"
                [timeOnly]="true"
                [showIcon]="true"
                hourFormat="12"
                appendTo="body"
                [placeholder]="'CAMPAIGN.BLACKOUT.START_TIME' | translate"
                aria-label="blackout-start-time-input"
                data-testid="blackout-start-time-input">
              </p-datepicker>
            </div>
            <div class="col p-0">
              <p-datepicker
                inputId="end_time"
                formControlName="end_time"
                inputStyleClass="w-full text-sm"
                [timeOnly]="true"
                [showIcon]="true"
                hourFormat="12"
                appendTo="body"
                [placeholder]="'CAMPAIGN.BLACKOUT.END_TIME' | translate"
                aria-label="blackout-end-time-input"
                data-testid="blackout-end-time-input">
              </p-datepicker>
            </div>
          </div>
        }

        @if (!SelectedType) {
          <div class="flex align-items-center gap-2 text-500 text-xs py-1">
            <i class="pi pi-info-circle text-xs"></i>
            <span>{{ 'CAMPAIGN.BLACKOUT.SELECT_TYPE_HINT' | translate }}</span>
          </div>
        }

        <!-- Applies To Days — only for Time type -->
        @if (SelectedType === 'Time') {
          <div class="flex flex-wrap gap-2" data-testid="blackout-days-container">
            @for (day of dayOptions; track day.value) {
              <button
                type="button"
                class="px-2 py-1 border-round text-xs font-medium cursor-pointer border-1 transition-all transition-duration-200"
                [class.bg-primary]="isDaySelected(day.value)"
                [class.text-white]="isDaySelected(day.value)"
                [class.border-primary]="isDaySelected(day.value)"
                [class.surface-card]="!isDaySelected(day.value)"
                [class.text-700]="!isDaySelected(day.value)"
                [class.border-300]="!isDaySelected(day.value)"
                (click)="toggleDay(day.value)"
                [attr.aria-label]="'blackout-day-' + day.value.toLowerCase() + '-button'"
                [attr.data-testid]="'blackout-day-' + day.value.toLowerCase() + '-button'">
                {{ 'CAMPAIGN.BLACKOUT.' + day.key | translate }}
              </button>
            }
          </div>
        }

        <!-- Recurring toggle — only for Date type -->
        @if (SelectedType === 'Date') {
          <div class="flex align-items-center gap-2">
            <p-toggleswitch
              inputId="is_recurring"
              formControlName="is_recurring"
              aria-label="blackout-is-recurring-toggle"
              data-testid="blackout-is-recurring-toggle">
            </p-toggleswitch>
            <label for="is_recurring" class="text-xs text-700 cursor-pointer"
              data-testid="blackout-is-recurring-label">
              {{ 'CAMPAIGN.BLACKOUT.IS_RECURRING' | translate }}
            </label>
          </div>
        }

        <!-- Descriptions row -->
        <div class="grid m-0 gap-3">
          <div [class]="isSecondaryLanguage() ? 'col p-0' : 'col-12 p-0'">
            <textarea
              id="description_pri_lang"
              pInputTextarea
              formControlName="description_pri_lang"
              class="w-full text-sm"
              rows="3"
              [placeholder]="isSecondaryLanguage()
                ? ('CAMPAIGN.BLACKOUT.DESCRIPTION_PRIMARY' | translate) + ' (' + primaryLanguage() + ')'
                : ('CAMPAIGN.BLACKOUT.DESCRIPTION_PRIMARY' | translate)"
              aria-label="blackout-description-primary-input"
              data-testid="blackout-description-primary-input">
            </textarea>
          </div>
          @if (isSecondaryLanguage()) {
            <div class="col p-0">
              <textarea
                id="description_sec_lang"
                pInputTextarea
                formControlName="description_sec_lang"
                class="w-full text-sm"
                rows="2"
                [placeholder]="('CAMPAIGN.BLACKOUT.DESCRIPTION_SECONDARY' | translate) + ' (' + secondaryLanguage() + ')'"
                aria-label="blackout-description-secondary-input"
                data-testid="blackout-description-secondary-input">
              </textarea>
            </div>
          }
        </div>

      </div>
    </form>
  }

  <!-- Footer -->
  <ng-template pTemplate="footer">
    <div class="flex justify-content-end gap-2 px-4 py-3 border-top-1 surface-border"
      data-testid="blackout-period-create-footer">
      <p-button
        [label]="'ADMIN.COMMON.CANCEL' | translate"
        severity="secondary"
        [outlined]="true"
        size="small"
        (onClick)="cancel()"
        aria-label="cancel-blackout-period-button"
        data-testid="cancel-blackout-period-button" />
      <p-button
        [label]="(activeConfig() ? 'ADMIN.COMMON.UPDATE' : 'ADMIN.COMMON.SAVE') | translate"
        [loading]="isSubmitting()"
        [disabled]="form.invalid"
        size="small"
        (onClick)="submit()"
        aria-label="save-blackout-period-button"
        data-testid="save-blackout-period-button" />
    </div>
  </ng-template>

</p-dialog>
